import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { useCopy } from './copy/copyMode';
import { WaitlistCTA } from './WaitlistCTA';
function FeatureItem({
  text
}: {
  text: string;
}) {
  return <li className="flex items-start gap-3 text-sm md:text-base text-[#2B211A]" data-id="element-1813">
      <div className="w-1.5 h-1.5 bg-[#2B211A] mt-2 shrink-0" data-id="element-1814" />
      <span className="leading-relaxed" data-id="element-1815">{text}</span>
    </li>;
}
export function PricingSection() {
  const copy = useCopy();
  return <section className="relative w-full bg-[#FAF8F4] py-16 md:py-32 overflow-hidden" data-id="element-1816">
      {/* Halftone dot-matrix texture — matches the other cream sections */}
      <div aria-hidden="true" className="absolute inset-0 dot-matrix opacity-40 pointer-events-none z-0" data-id="element-1817" />
      <div className="max-w-[1280px] mx-auto px-6 md:px-10 relative z-10" data-id="element-1818">
        {/* Header */}
        <div className="mb-12 md:mb-16 text-center md:text-left" data-id="element-1819">
          <h2 className="text-3xl md:text-5xl font-title font-medium text-[#2B211A] tracking-tight leading-[1.1]" data-id="element-1820">
            {copy.pricingSection.headline}
          </h2>
        </div>

        {/* Grid Container */}
        <div className="relative border-t border-b border-dotted border-neutral-300" data-id="element-1821">
          {/* Vertical dotted lines for desktop grid */}
          <div className="hidden md:block absolute top-0 bottom-0 left-1/3 w-px border-l border-dotted border-neutral-300" data-id="element-1822" />
          <div className="hidden md:block absolute top-0 bottom-0 left-2/3 w-px border-l border-dotted border-neutral-300" data-id="element-1823" />
          <div className="hidden md:block absolute top-0 bottom-0 left-0 w-px border-l border-dotted border-neutral-300" data-id="element-1824" />
          <div className="hidden md:block absolute top-0 bottom-0 right-0 w-px border-l border-dotted border-neutral-300" data-id="element-1825" />

          <div className="grid grid-cols-1 md:grid-cols-3 relative z-10" data-id="element-1826">
            {/* Starter Tier — paid, opens with a 7-day free trial */}
            <div className="p-6 md:p-8 border-b border-dotted border-neutral-300 md:border-b-0 flex flex-col" data-id="element-1827">
              <div className="bg-[#F3EFEA] rounded-2xl p-6 md:p-8 mb-8 h-48 flex flex-col justify-between" data-id="element-1828">
                <h3 className="font-title font-normal text-2xl text-[#2B211A]" data-id="element-1829">
                  Starter
                </h3>
                <div data-id="element-1830">
                  <div className="text-neutral-500 text-lg" data-id="element-1831">
                    <span className="text-2xl text-[#2B211A]" data-id="element-1832">$49</span>
                    /month
                  </div>
                  <span className="inline-flex mt-2 items-center gap-1.5 text-xs font-semibold text-[#EA580C] bg-[#FBEAE0] px-2.5 py-1 rounded-full" data-id="element-1833">
                    7-day free trial
                  </span>
                </div>
              </div>
              <ul className="space-y-3 mb-10 flex-1" data-id="element-1834">
                <FeatureItem text="1 workspace, unlimited members" data-id="element-1835" />
                <FeatureItem text="Starter monthly credit allowance" data-id="element-1836" />
                <FeatureItem text="Slack-native agent in threads + mentions" data-id="element-1837" />
                <FeatureItem text="Integrations + tool execution" data-id="element-1838" />
                <FeatureItem text="Scheduled tasks & crons" data-id="element-1839" />
                <FeatureItem text="Drafts + artifacts where supported" data-id="element-1840" />
              </ul>
              <WaitlistCTA className="w-full flex justify-center bg-[#EA580C] hover:bg-[#C2410C] text-white py-3 rounded-xl font-medium transition-colors" data-id="element-1841">
                Start 7-day trial
              </WaitlistCTA>
            </div>

            {/* Pro Tier — paid, opens with a 7-day free trial */}
            <div className="p-6 md:p-8 border-b border-dotted border-neutral-300 md:border-b-0 flex flex-col" data-id="element-1842">
              <div className="bg-[#F3EFEA] rounded-2xl p-6 md:p-8 mb-8 h-48 flex flex-col justify-between" data-id="element-1843">
                <h3 className="font-title font-normal text-2xl text-[#2B211A]" data-id="element-1844">
                  Pro
                </h3>
                <div data-id="element-1845">
                  <div className="text-neutral-500 text-lg" data-id="element-1846">
                    <span className="text-2xl text-[#2B211A]" data-id="element-1847">$199</span>
                    /month
                  </div>
                  <span className="inline-flex mt-2 items-center gap-1.5 text-xs font-semibold text-[#EA580C] bg-[#FBEAE0] px-2.5 py-1 rounded-full" data-id="element-1848">
                    7-day free trial
                  </span>
                </div>
              </div>
              <ul className="space-y-3 mb-10 flex-1" data-id="element-1849">
                <FeatureItem text="Multiple workspaces, unlimited members" data-id="element-1850" />
                <FeatureItem text="Higher monthly credit allowance" data-id="element-1851" />
                <FeatureItem text="Slack-native agent in threads + mentions" data-id="element-1852" />
                <FeatureItem text="Integrations + tool execution" data-id="element-1853" />
                <FeatureItem text="Scheduled tasks & crons" data-id="element-1854" />
                <FeatureItem text="Drafts + artifacts where supported" data-id="element-1855" />
              </ul>
              <WaitlistCTA className="w-full flex justify-center bg-[#2B211A] hover:bg-[#1C1510] text-white py-3 rounded-xl font-medium transition-colors" data-id="element-1856">
                Start 7-day trial
              </WaitlistCTA>
            </div>

            {/* Custom Tier — enterprise, contact sales */}
            <div className="p-6 md:p-8 flex flex-col" data-id="element-1857">
              <div className="bg-[#F3EFEA] rounded-2xl p-6 md:p-8 mb-8 h-48 flex flex-col justify-between" data-id="element-1858">
                <div data-id="element-1859">
                  <span className="block text-[11px] font-bold tracking-[2px] uppercase text-[#EA580C] mb-2" data-id="element-1860">
                    Enterprise
                  </span>
                  <h3 className="font-title font-normal text-2xl text-[#2B211A]" data-id="element-1861">
                    Custom
                  </h3>
                </div>
                <div className="text-neutral-500 text-lg" data-id="element-1862">Flexible pricing</div>
              </div>
              <ul className="space-y-3 mb-10 flex-1" data-id="element-1863">
                <FeatureItem text="Everything in Pro" data-id="element-1864" />
                <FeatureItem text="Invoicing + custom billing terms" data-id="element-1865" />
                <FeatureItem text="Security review support + DPA" data-id="element-1866" />
                <FeatureItem text="SLA + priority support" data-id="element-1867" />
                <FeatureItem text="Dedicated onboarding + tailored limits/controls" data-id="element-1868" />
              </ul>
              <Link to="/contact" className="w-full flex justify-center bg-[#2B211A] hover:bg-[#1C1510] text-white py-3 rounded-xl font-medium transition-colors" data-id="element-1869">
                Contact sales
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>;
}